// THE FLORA FORGE — procedural plant cards, and the kit that assembles them.
//
// WHY THIS FILE EXISTS. Every previous vegetation pass in this project made
// the same mistake in a different costume: ONE PLANT PART PER POLYGON. First
// it was a cone per bush, then (wave 10 r5) a textured card per BLADE. The
// second was a real improvement and it still delivered the shot the founder
// rejected — an eye-level fen where the ground carries a scatter of enormous
// separated leaves, each one unmistakably a quad, with bare mud between them.
// The arithmetic is why: a card per blade means a knee-high tussock costs
// seventeen quads for seventeen leaves, so the only affordable density is a
// density that reads as succulents on a lawn. The delivered frame ran 1.3
// MILLION triangles of foliage at 64 fps and still showed bare peat.
//
// The industry answer, and the one this file implements, is that the ATLAS
// CARD IS THE CLUSTER. One quad carries a painted tuft of sixteen blades, or
// a whole pinnate fern frond, or a branch's worth of leaves — coverage,
// midribs, tips and the gaps between them all resolved in texture space,
// where they cost fill rate instead of geometry. A three-card star is then a
// forty-blade tussock for twenty-four triangles, which is a twelfth of the
// per-blade cost of the kit it replaces. That is what buys the density §e
// asks for ("near-camera framing stays dense and full-coverage — no bare
// terrain texture in frame", reference t133/t147).
//
// Three rules hold the sheet together:
//
//   1. STRICTLY GREYSCALE RGB, coverage in alpha. The sheet is used as a
//      `map`, and a map multiplies all three channels — the biome hue
//      families (render/atmosphere.ts) own the palette and must survive.
//      A first cut of the wave-10 blade sheet packed a translucency weight
//      into green and turned every tussock in the flats into an orange
//      butterfly.
//   2. DARK MEANS THIN. The shading channel is painted bright along ribs and
//      stems and dark out at the margins and tips, so the translucency term
//      below can reconstruct leaf thickness from the map alone with no extra
//      texture fetch and no extra UV set (which an ATLAS could not have
//      afforded anyway — the old term read `vMapUv.y` for tip-ness, and in
//      an atlas that is the cell's row, not the leaf's length).
//   3. EVERY CARD IS PAINTED ON THE SAME CANVAS, ONCE, AND CACHED at module
//      level. Generation is ~40 ms of 2D drawing; a scene teardown must not
//      pay it twice, so the atlas outlives Tracker disposal exactly the way
//      render/forge.ts's surface cache does.

// ---------------------------------------------------------------------------
// The atlas
//
// THE SHEET LEFT THE BROWSER IN PHASE 3. Everything between here and the card
// kit below used to be six hundred lines of 2D canvas painting: the blade, the
// leaf, the pinnate frond, the tuft, the canopy mass and the sixteen cells
// they compose. It ran at load, on a player's machine, and it produced a
// STRICTLY GREYSCALE sheet with coverage in alpha — because a canvas backing
// store is premultiplied at 8 bits and because a `map` multiplies all three
// channels, so one byte had to be the leaf's albedo, its thickness AND its
// relief at once, and the shader untangled them with "dark means thin".
//
// The painters are now `@bebop/forge`'s src/foliage.ts, drawn on an offline
// scanline rasteriser (src/raster.ts — no DOM, no dependencies), baked to
// KTX2 by tools/assets and loaded by render/assets.ts before the game boots.
// Every silhouette is the one that shipped. What changed is what a texel
// carries:
//
//   R  SHADE      the value multiplied into the leaf's albedo, including the
//                 depth darkening that gives a painted crown an inside.
//   G  THINNESS   1 at a margin, 0 at a stem — PAINTED, not inferred.
//   B  VEIN       geometric relief only, so the per-blade bump no longer
//                 draws a false gradient round every depth-darkened leaf.
//   A  COVERAGE   4×-sampled, with the colour bled past the cut so no mip
//                 drags a silhouette toward black.
//
// The judges measured the old packing's cost directly: "flat untextured
// vector shapes at one value, median 40x40 frond span 130 against 202–205 in
// the reference". Separating thinness from shade is what lets a frond's
// margin glow when the sun is behind it WITHOUT its shadowed pinnae glowing
// just as hard — which is the failure the transmission term's own comment
// records from r11-t5 ("the whole thicket flattens to bone").

import { FLORA_CELLS, type FloraCell, floraCellRect } from "@bebop/forge";
import * as THREE from "three";
import { bakedFoliage } from "./assets";

export { FLORA_CELLS, type FloraCell };

export interface FloraAtlas {
  readonly texture: THREE.Texture;
  /** [u0, v0, u1, v1] of a cell, in atlas UV space (v up). */
  rect(cell: FloraCell): readonly [number, number, number, number];
}

let cached: FloraAtlas | undefined;

/**
 * The baked atlas.
 *
 * NOT TRACKED and never disposed — one sheet, shared by every plant in every
 * scene, uploaded once by the boot gate. Same contract as render/forge.ts's
 * surface cache and for the same reason.
 */
export function floraAtlas(): FloraAtlas {
  if (cached) return cached;
  const texture = bakedFoliage();
  if (!texture) {
    // Loud, like every other baked asset: there is no painter left in this
    // bundle, so a missing atlas is a boot-order bug and not a thing to
    // paper over with an empty texture.
    throw new Error(
      "the vegetation atlas was not loaded; the boot gate should have awaited loadBakedAssets()",
    );
  }
  cached = { texture, rect: floraCellRect };
  return cached;
}

// ---------------------------------------------------------------------------
// The card kit: geometry.

export interface CardOpts {
  /** Card size in metres. */
  width: number;
  height: number;
  /** Lean off vertical (rad) and bearing around the clump (rad). */
  tilt?: number;
  spin?: number;
  /** Root height above the clump origin, metres. */
  lift?: number;
  /** Lateral offset of the root from the clump axis, metres. */
  offset?: number;
  /** Backward droop of the tip, as a fraction of height. */
  droop?: number;
  /** Cross-card cupping, as a fraction of width. A flat quad is the whole
   *  "it is obviously a polygon" tell; a cupped one catches the key light
   *  across its face and its silhouette stops being a straight line. */
  cup?: number;
  /** Columns / rows. Two columns is the minimum that can cup. */
  cols?: number;
  rows?: number;
  /** Blend the shading normal toward this direction (0 = keep the card's
   *  own flat normal, 1 = fully replace). */
  normalUp?: number;
  normalOut?: number;
  /** Clump centre the "out" normal points away from. */
  centerY?: number;
  /** Base→tip vertex-colour gradient (multiplies the instance tint). */
  shadeLo?: number;
  shadeHi?: number;
  /** Per-card hue nudge, as a linear RGB multiplier triple. Small: the
   *  §c S ≤ 13 rendered-mean law is a whole-frame law, and this is how a
   *  clump gets a yellowed blade next to a green one without breaking it. */
  hue?: readonly [number, number, number];
  /** Mirror the card in u — one painted tuft, two silhouettes. */
  flip?: boolean;
}

/**
 * One atlas card as world geometry, already posed in clump space.
 *
 * The card is built lying in XY (v up), cupped and drooped, then leaned by
 * `tilt`, swung to `spin` and pushed out to `offset`. Vertex colours carry
 * the vertical gradient and the per-card hue; the normal is blended toward
 * up (grass) or outward (a bush or a crown), which is what turns a fan of
 * quads into something the sun models as a volume instead of as a fence.
 */
export function floraCard(atlas: FloraAtlas, cell: FloraCell, o: CardOpts): THREE.BufferGeometry {
  const cols = o.cols ?? 2;
  const rows = o.rows ?? 2;
  const g = new THREE.PlaneGeometry(o.width, o.height, cols, rows);
  const pos = g.getAttribute("position");
  const uv = g.getAttribute("uv");
  const [u0, v0, u1, v1] = atlas.rect(cell);
  const droop = (o.droop ?? 0.16) * o.height;
  const cup = (o.cup ?? 0.22) * o.width;
  const flip = o.flip ?? false;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i);
    const y = pos.getY(i);
    const t = (y + o.height / 2) / o.height; // 0 base → 1 tip
    const s = (x / o.width) * 2; // −1 … 1 across the card
    // Droop grows quadratically (a leaf falls away from its stem slowly at
    // first) and the cup relaxes toward the tip where a real blade opens.
    pos.setZ(i, pos.getZ(i) - t * t * droop + cup * (1 - s * s) * (1 - t * 0.55));
    // Narrow the very tip beyond what the alpha cut does: overlapping cards
    // must never form a flat wall.
    pos.setX(i, x * (1 - t * 0.1));
    pos.setY(i, y + o.height / 2);
    const uu = flip ? 1 - uv.getX(i) : uv.getX(i);
    uv.setXY(i, u0 + uu * (u1 - u0), v0 + uv.getY(i) * (v1 - v0));
  }
  pos.needsUpdate = true;
  uv.needsUpdate = true;
  g.computeVertexNormals();

  // Normal shaping, before the pose rotations so "up" and "out" are the
  // clump's, not the card's.
  const nrm = g.getAttribute("normal");
  const up = o.normalUp ?? 0;
  const out = o.normalOut ?? 0;
  if (up > 0 || out > 0) {
    const cy = o.centerY ?? o.height * 0.4;
    const v = new THREE.Vector3();
    const target = new THREE.Vector3();
    for (let i = 0; i < nrm.count; i++) {
      v.set(nrm.getX(i), nrm.getY(i), nrm.getZ(i));
      if (out > 0) {
        target.set(pos.getX(i), pos.getY(i) - cy, pos.getZ(i));
        if (target.lengthSq() < 1e-8) target.set(0, 1, 0);
        target.normalize();
        v.lerp(target, out);
      }
      if (up > 0) v.lerp(target.set(0, 1, 0), up);
      v.normalize();
      nrm.setXYZ(i, v.x, v.y, v.z);
    }
    nrm.needsUpdate = true;
  }

  // Vertex colour: base→tip gradient × per-card hue.
  const lo = o.shadeLo ?? 0.55;
  const hi = o.shadeHi ?? 1.25;
  const [hr, hg, hb] = o.hue ?? [1, 1, 1];
  const colors = new Float32Array(pos.count * 3);
  for (let i = 0; i < pos.count; i++) {
    const t = Math.min(1, Math.max(0, pos.getY(i) / o.height));
    const b = lo + (hi - lo) * t;
    colors[i * 3] = b * hr;
    colors[i * 3 + 1] = b * hg;
    colors[i * 3 + 2] = b * hb;
  }
  g.setAttribute("color", new THREE.BufferAttribute(colors, 3));

  // Pose: tip the top OUT (about X, which for a card facing +Z leans it
  // away from the clump axis), push it out to its radius along the same
  // axis, then swing it to its bearing. Offsetting along +Z rather than +X
  // is what makes a fan of cards read as a bush — each panel ends up tangent
  // to the clump and facing outward, which is the arrangement every foliage
  // kit in the genre uses. Offsetting along X instead leans every card
  // sideways to its own radius and the clump reads as a pinwheel.
  if (o.tilt) g.rotateX(o.tilt);
  if (o.offset) g.translate(0, 0, o.offset);
  if (o.spin) g.rotateY(o.spin);
  if (o.lift) g.translate(0, o.lift, 0);
  return g;
}

// ---------------------------------------------------------------------------
// The foliage material.

/**
 * Backlit-foliage translucency (§e: "diffuse backlit mist between trunks +
 * foliage translucency", reference t108/t145/t147).
 *
 * Standard GGX has no transmission, and the reference's forest read depends
 * entirely on light coming THROUGH leaves. Two lobes, doing two different
 * jobs, both modulated by leaf THICKNESS — and thickness is read straight
 * off the shading channel, which the atlas paints bright along ribs and
 * stems and dark out at margins and tips (rule 2 in the header). A uniform
 * wrap makes a plant glow like wax; this makes it glow at its edges, which
 * is what a backlit fern actually does.
 *
 *   WRAP — `dot(-N, L)`, squared. The leaf's far side is lit, so its near
 *   side is not black. View-independent, and it is what shipped through
 *   wave 10.
 *
 *   FORWARD SCATTER (wave 11) — `dot(-V, L)`, i.e. how nearly the lens is
 *   looking back up the light's own path through the leaf. This is the term
 *   the §e signature actually names. Wrap alone says "backlit leaves are
 *   less dark"; a forest reads backlit because they are BRIGHTER THAN THE
 *   SKY BEHIND THEM when you look toward the sun, and only a view-dependent
 *   lobe can do that. It is also why the term was invisible until now: the
 *   fen's key was staged 116° behind the lens (see render/atmosphere.ts),
 *   so there was no such view anywhere in the biome.
 *
 * The light that comes THROUGH a leaf is not the colour that bounces off
 * one — chlorophyll passes yellow-green and eats red — so the forward lobe
 * carries its own transmission tint rather than the albedo.
 *
 * Exponent 6 on the forward lobe is what keeps this a forest term. At the
 * flats vista the lens is 125° off the key, which puts the lobe at 0.017:
 * the desert frames the judges passed move by nothing. Facing the fen's own
 * sun it is 0.74.
 *
 * BOTH LOBES ARE NORMALISED BY THE LAMBERT CONSTANT as of wave 11, and that
 * is a bug fix, not a taste change. `uWrap` has always been documented as a
 * transmission fraction and has never been divided by π, so a coefficient
 * that read 0.78 was actually returning 2.45× the diffuse response of the
 * same leaf lit from the front: a backlit fern was not translucent, it was
 * emissive. Nothing in the project showed it, because until this wave no
 * vista anywhere had the key behind a leaf — the fen's sun was staged 116°
 * behind its own lens and every other biome's foliage is sparse and small
 * in frame. It reads as a fraction of Lambert now, which is what the
 * numbers say, and what the physics allows (reflected + transmitted ≤ 1).
 *
 * It only ever brightens, so no §c palette anchor can fall below its band
 * because of it, and it costs no extra pass and no extra texture fetch.
 */
export function foliageMaterial(
  atlas: FloraAtlas,
  opts: {
    color: number;
    emissive?: number;
    /** 0 disables both translucency lobes (the frozen night regime). */
    wrap?: number;
    /** Forward-scatter strength, relative to `wrap`. 0 = wrap only. */
    through?: number;
    /** Per-channel GAIN on the leaf's own albedo for the light that passes
     *  through it rather than off it: chlorophyll eats red and passes
     *  yellow-green, and a thin leaf transmits a little more than it
     *  bounces. A multiplier, never a replacement — see the shader note. */
    throughColor?: readonly [number, number, number];
    alphaTest?: number;
    /** Resolve the alpha cut against the MSAA sample mask. See below. */
    coverage?: boolean;
    /**
     * WAVE 12 — THE LEAF SURFACE. Two numbers turn a painted card into a
     * material, and neither of them costs a texture fetch.
     *
     * `relief` bumps the shading normal off the atlas's own shading channel,
     * which rule 2 already guarantees is a height field (bright rib, dark
     * margin). The gradient comes from `dFdx`/`dFdy` of the albedo fetch the
     * fragment has ALREADY made, so a per-blade normal is arithmetic, not
     * bandwidth — which matters, because the fen carpet is fragment-bound at
     * several alpha-tested layers per pixel and a second sheet would have
     * been the one thing this vista cannot afford (see ScatterLayer's
     * fill-rate note).
     *
     * `gloss` drops roughness off 1.0. A leaf cuticle is WAXY, and the
     * reference's forest peak (t133/t135/t147) gets most of its Y>200 tail
     * from grazing specular on leaf faces with the sun beyond them, not from
     * diffuse — at roughness 1 that lobe does not exist, which is precisely
     * why every measured highlight in the fen lived in the sky.
     */
    relief?: number;
    gloss?: number;
    /**
     * ALBEDO GAIN — and this is the one number in the file that was a plain
     * bug rather than a missing feature.
     *
     * The atlas is bound as `map`, and a map MULTIPLIES the material colour.
     * Its RGB is the shading/height channel, painted between 0.30 at a leaf
     * margin and 0.95 along a rib — so every plant in the game has been
     * rendering at roughly HALF the albedo its palette entry says, and every
     * light-dependent term in this shader (Lambert, both translucency lobes,
     * the margin glow) has been scaled by that same half. The project's own
     * material forge fixed this for architecture two waves ago and states
     * the rule outright: "the sheet arrives normalised to a mean of 1.0 and
     * MULTIPLIES … what changes is only that the value now varies per pixel,
     * which is the entire definition of a material" (render/attire.ts). The
     * flora sheet never got the divisor.
     *
     * Putting it back is what finally moved the measured highlight tail: the
     * frame is ACES-graded, its shoulder is steep, and no amount of extra
     * rim coefficient can push display 198 past 200 when the scene-referred
     * value behind it is half of what the palette intended.
     */
    gain?: number;
    /** Sharpness of the forward-scatter lobe. Lower is broader. */
    throughPower?: number;
    /** Margin glow: how hard a thin, backlit edge lights up on its own.
     *  This is the "rim-lit needle" term — it needs no view/sun alignment,
     *  only a leaf whose far side is toward the light. */
    rim?: number;
  },
): THREE.MeshStandardMaterial {
  const wrap = opts.wrap ?? 0;
  const through = opts.through ?? 0;
  const throughColor = opts.throughColor ?? ([1.32, 1.5, 0.78] as const);
  const relief = opts.relief ?? 0;
  const gloss = opts.gloss ?? 0;
  const rim = opts.rim ?? 0;
  const gain = opts.gain ?? 1;
  const throughPower = opts.throughPower ?? 6;
  const mat = new THREE.MeshStandardMaterial({
    color: opts.color,
    map: atlas.texture,
    alphaTest: opts.alphaTest ?? 0.4,
    // WAVE 14 — ALPHA-TO-COVERAGE IS NOW OPT-IN, AND THE FEN'S FRAME RATE
    // IS THE REASON. A2C makes an alpha-tested edge resolve against the
    // MSAA sample mask, which is genuinely better looking and genuinely
    // expensive: it costs a per-sample coverage evaluation on every
    // foliage fragment in a scene that is several alpha-tested layers deep
    // over most of the plate. Measured on the fen vista, turning it off
    // wholesale: 57.4 → 60.9 fps.
    //
    // So it stays ON where a soft alpha edge is what the viewer is looking
    // at — the hero kinds, whose fronds are 200 px of frame — and OFF for
    // the ground carpet's grass and litter and for the two distance
    // impostors, where the entire card is a few pixels across, its alpha
    // edge is already sub-pixel, and MSAA is antialiasing the quad's own
    // outline regardless. `hero > 0` is exactly that split and it already
    // exists (see ScatterLayer's leafMat).
    alphaToCoverage: opts.coverage ?? true,
    // A cuticle, not chalk. Kept at 1 when `gloss` is 0 so every kind that
    // does not opt in renders byte-for-byte as it did before this wave.
    roughness: 1 - gloss,
    // THE SUN, SEEN IN A LEAF. The scene probe (render/atmosphere.ts) is an
    // equirect sky with a genuinely unclipped HDR sun disc in it, held at
    // environmentIntensity 0.17 because its DIFFUSE half is a flat ambient
    // nobody wants. Its SPECULAR half is the opposite: it is the only thing
    // in the frame that can put a 250 on a leaf, which is exactly what the
    // reference's forest peak is made of and exactly what the fen has never
    // had. Per-material envMapIntensity is the recipe §e already uses for
    // hero metal (windows, skiff brass); this is the same trick pointed at
    // a cuticle. It costs nothing — the PMREM sample is already in the
    // fragment for every standard material in the scene.
    envMapIntensity: gloss > 0 ? 6 : 1,
    side: THREE.DoubleSide,
    vertexColors: true,
    emissive: opts.emissive ?? 0x000000,
    emissiveIntensity: opts.emissive ? 0.2 : 0,
  });
  // NO EARLY-OUT ANY MORE, and this is load-bearing. The old sheet was
  // greyscale, so a material that wanted none of the effects below could take
  // three's stock `map_fragment` and be byte-identical. This sheet is not
  // greyscale: its green is a thickness and its blue is a height, and stock
  // `map_fragment` would multiply both into the albedo — turning every plant
  // that opts out (the frozen night regime) a flat cyan.
  // EVERY COEFFICIENT IS A LITERAL, NOT A UNIFORM. These are fixed the moment
  // the material is built (one per kind per palette), and the vista that has
  // to pay for this shader is fragment-bound at several alpha-tested layers
  // per pixel. As literals the compiler folds the dead half away entirely:
  // the ground carpet, which opts out of relief and rim, ends up with the
  // exact instruction stream it had before this wave rather than two runtime
  // branches it always fails. `customProgramCacheKey` below already keys on
  // the values, so no two recipes can share a compiled program.
  const f = (n: number): string => (Number.isInteger(n) ? `${n}.0` : `${n}`);
  const decl =
    `const float uWrap = ${f(wrap)};\n` +
    `const float uThrough = ${f(through)};\n` +
    `const float uRelief = ${f(relief)};\n` +
    `const float uRim = ${f(rim)};\n` +
    `const float uThroughPow = ${f(throughPower)};\n` +
    `const float uLeafGain = ${f(gain)};\n` +
    `const vec3 uThroughColor = vec3( ${throughColor.map(f).join(", ")} );`;
  mat.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>\n${decl}`)
      // The divisor. After `map_fragment`, so it lands on the sheet-modulated
      // colour and every lighting term downstream inherits it; the alpha the
      // cutout tests is a different channel and is untouched.
      // THE ONE FETCH. Everything this material needs from the atlas comes
      // out of a single texel: shade in R, thinness in G, vein height in B,
      // coverage in A. `map_fragment` would multiply all four channels into
      // the diffuse — which was correct when the sheet was greyscale and is
      // catastrophic now that G and B are geometry — so the fetch is done
      // here instead and the three fields are taken apart by name.
      //
      // The fen carpet is fragment-bound at several alpha-tested layers per
      // pixel, and the old shader fetched `map` TWICE (once in map_fragment
      // and once for the thickness). One fetch that answers three questions
      // is why the extra channels cost nothing.
      .replace(
        "#include <map_fragment>",
        /* glsl */ `
        vec4 bebopLeaf = texture2D( map, vMapUv );
        diffuseColor.a *= bebopLeaf.a;
        diffuseColor.rgb *= bebopLeaf.r * uLeafGain;
        `,
      )
      // PER-BLADE RELIEF, injected after three.js has finished building
      // `normal` and before anything reads it. Mikkelsen's unparametrised
      // bump: the surface gradient of the height field is reconstructed from
      // screen-space derivatives of the height and of the view position, so
      // it needs no tangents, no second UV set and no second texture. The
      // gradient is clamped because at an alpha-tested silhouette one lane
      // of the 2x2 quad is a discarded texel and the raw derivative there is
      // a spike, which would draw a bright wire round every leaf.
      .replace(
        "#include <lights_fragment_begin>",
        /* glsl */ `
        // The relief channel — GEOMETRIC height, not the shading value. The
        // difference shows exactly where a crown's near and far leaves meet:
        // under the old packing that boundary was a step in the height field
        // (because the far leaf was painted a stop down), so the bump drew a
        // bright wire round every one of the 44% of leaves that carry the
        // depth cue. They are coplanar; now the height says so.
        #if defined( USE_MAP )
        float bebopH = bebopLeaf.b;
        if ( uRelief > 0.0 ) {
          vec3 sx = dFdx( - vViewPosition );
          vec3 sy = dFdy( - vViewPosition );
          float hx = clamp( dFdx( bebopH ), -0.25, 0.25 );
          float hy = clamp( dFdy( bebopH ), -0.25, 0.25 );
          vec3 r1 = cross( sy, normal );
          vec3 r2 = cross( normal, sx );
          float det = dot( sx, r1 );
          if ( abs( det ) > 1e-9 ) {
            vec3 grad = sign( det ) * ( hx * r1 + hy * r2 );
            normal = normalize( abs( det ) * normal - uRelief * grad );
          }
        }
        #endif
        #include <lights_fragment_begin>
        `,
      )
      .replace(
        "#include <lights_fragment_end>",
        /* glsl */ `
        #include <lights_fragment_end>
        #if ( NUM_DIR_LIGHTS > 0 )
        {
          #ifdef USE_MAP
            // THINNESS, READ NOT GUESSED. This used to be
            // clamp( ( 0.9 - shade ) * 2.4 ) — "dark means thin" — which is
            // true of a margin, false of a shaded leaf, and false of a
            // dark twig. The painters now state it per texel: a margin is
            // 0.86–0.95, a lamina 0.72–0.84, a midrib 0.14, a rachis 0.04,
            // a twig 0.02. The remapping is the one the wave-12 numbers
            // were tuned against and is unchanged, so a texel that used to
            // resolve to the same thinness still does.
            float thin = 0.24 + bebopLeaf.g * 1.05;
          #else
            float thin = 1.0;
          #endif
          vec3 bebopL = directionalLights[ 0 ].direction;
          float back = max( 0.0, dot( -normal, bebopL ) );
          reflectedLight.directDiffuse +=
            diffuseColor.rgb * directionalLights[ 0 ].color
            * RECIPROCAL_PI * uWrap * thin * back * back;
          // The lens looking back up the light's path: vViewPosition points
          // at the camera, the light's own travel is -L, so the two agree
          // when the sun is beyond the leaf from here.
          float bebopFwd = max( 0.0, dot( normalize( -vViewPosition ), bebopL ) );
          float glow = pow( bebopFwd, uThroughPow );
          // THE MARGIN GLOW (wave 12). A needle or a frond tip lights up
          // when the sun is behind it whether or not the lens happens to be
          // looking up the sun's own path — that is what stipples the
          // reference's Y>=200 mask below its treeline. So it rides the
          // thinness (which peaks exactly at the margins and tips the painters
          // darken) and the leaf's BACKNESS, and it is squared on top of
          // grazing view so it stays an EDGE rather than a wash: at
          // dot(N,V)=0.9 it contributes 1% of its peak.
          //
          // LINEAR in thinness, not squared. The first cut squared it, and
          // on a sedge blade — narrow, so thin is at its ceiling over the
          // whole card, and edge-on, so the grazing term is 1 — the rim came
          // out at three times Lambert and the reed bed rendered as a stand
          // of white spikes. A rim may be bright; it may not out-light the
          // sun.
          float bebopGraze = 1.0 - abs( dot( normal, normalize( vViewPosition ) ) );
          bebopGraze *= sqrt( bebopGraze );
          reflectedLight.directDiffuse +=
            diffuseColor.rgb * uThroughColor * directionalLights[ 0 ].color
            * RECIPROCAL_PI * uRim * thin * back * bebopGraze;
          // Transmission is the leaf's OWN pigment, warmed — not a colour of
          // its own. A constant tint here is an emitter: it does not go dark
          // where the plant is dark, so a shaded frond in a backlit frame
          // glows as hard as a lit one and the whole thicket flattens to
          // bone (measured at r11-t5: fern mass Y181 against a reference
          // forest ground that means 57–81). Times a small gain, because a
          // thin leaf does pass slightly more than it bounces.
          reflectedLight.directDiffuse +=
            diffuseColor.rgb * uThroughColor * directionalLights[ 0 ].color
            * RECIPROCAL_PI * uWrap * uThrough * thin * glow;
        }
        #endif
        `,
      );
  };
  mat.customProgramCacheKey = () =>
    `bebop-foliage-v3-${wrap}-${through}-${relief > 0 ? 1 : 0}-${gain}-${throughColor.join(",")}`;
  return mat;
}

/** Folded Blender leaves have real edges and ribs, so neither atlas cutouts nor
 * texture-derived normals belong on them. One opaque double-sided draw keeps
 * the carpet's early depth rejection; a small backlight term preserves the
 * fen's thin-leaf response without the old card shader's texture work. */
export function authoredFoliageMaterial(
  color: number,
  roughness = 0.94,
  backlight = 0.24,
): THREE.MeshStandardMaterial {
  const material = new THREE.MeshPhysicalMaterial({
    color,
    roughness,
    metalness: 0,
    specularIntensity: 0.12,
    vertexColors: true,
    side: THREE.DoubleSide,
    envMapIntensity: 0.25,
  });
  material.onBeforeCompile = (shader) => {
    shader.fragmentShader = shader.fragmentShader.replace(
      "#include <lights_fragment_end>",
      `#include <lights_fragment_end>
      #if ( NUM_DIR_LIGHTS > 0 )
        float natureBack = max(0.0, dot(-normal, directionalLights[0].direction));
        reflectedLight.directDiffuse += diffuseColor.rgb * directionalLights[0].color
          * RECIPROCAL_PI * ${backlight.toFixed(3)} * natureBack * natureBack;
      #endif`,
    );
  };
  material.customProgramCacheKey = () => `bebop-authored-foliage-${backlight}`;
  return material;
}
